import { AuctionItemSell, BidItem, InventoryManagementSearchResult, InventoryManagementSellerOffer } from 'remarketing-angular-library';
import { OfferHistory } from './offer-history.model';

export class OfferModalItem {
  auctionItemUuid: string;
  buyerPurchaseStatus?: string;
  buyerAuctionItemStatus?: string;
  buyerOeUuid: string;
  leadBuyerOeUuid?: string;
  financeSourceUuid?: string;
  financeSourceAch?: boolean;
  localDelivery: boolean;
  modelYear: number;
  make: string;
  model: string;
  trim: string;
  vin: string;
  offerHistory?: OfferHistory;
  inventoryItemUuid?: string;
  hasSpareKey?: boolean;

  /**
   * The vehicle name without a VIN tail, for the all-in identification block (M4.14) that puts the VIN
   * on its own line beneath the title. The offer-side twin of BidModalItem.titleSimple.
   *
   * Any absent part is skipped rather than interpolated, so a missing modelYear (or a make, model or trim
   * nulled after construction) never prints "null" and never leaves a stray leading or doubled space.
   *
   * Lives on the model, not on each offer action, so Make Offer and Follow-Up cannot drift apart. The
   * components' vehicleTitle getters stay where they are: they carry the ' - VIN ' run-up the flag-off
   * prose needs mid-sentence, and that copy is unchanged.
   */
  get titleSimple(): string {
    return [this.modelYear, this.make, this.model, this.trim]
      .filter(part => part !== null && part !== undefined && `${part}`.trim() !== '')
      .join(' ');
  }

  constructor(init: Omit<OfferModalItem, 'titleSimple'>) {
    this.auctionItemUuid = init.auctionItemUuid || null;
    this.buyerOeUuid = init.buyerOeUuid || null;
    this.leadBuyerOeUuid = init.leadBuyerOeUuid || null;
    this.buyerPurchaseStatus = init.buyerPurchaseStatus || null;
    this.buyerAuctionItemStatus = init.buyerAuctionItemStatus || null;
    this.financeSourceUuid = init.financeSourceUuid || null;
    this.financeSourceAch = init.financeSourceAch || null;
    this.localDelivery = init.localDelivery || null;
    this.modelYear = init.modelYear || null;
    this.make = init.make || '';
    this.model = init.model || '';
    this.trim = init.trim || '';
    this.vin = init.vin || '';
    this.offerHistory = init.offerHistory || null;
    this.inventoryItemUuid = init.inventoryItemUuid || null;
    this.hasSpareKey = !!init.hasSpareKey;
  }

  static fromBidItem(bidItem: BidItem): OfferModalItem {
    const {
      auctionItemUuid,
      buyerPurchaseStatus,
      buyerAuctionItemStatus,
      buyerOeUuid,
      financeSourceUuid,
      financeSourceAch,
      localDelivery,
      itemDetail = { modelYear: null, make: null, model: null, trim: null, vin: null },
      inventoryItemUuid,
      hasSpareKey
    } = bidItem;
    const { modelYear, make, model, trim, vin } = itemDetail;
    return new OfferModalItem({
      auctionItemUuid,
      buyerPurchaseStatus,
      buyerAuctionItemStatus,
      buyerOeUuid,
      financeSourceUuid,
      financeSourceAch,
      localDelivery,
      modelYear,
      make,
      model,
      trim,
      vin,
      inventoryItemUuid,
      hasSpareKey
    });
  }

  static fromAuctionItemSell(auctionItemSell: AuctionItemSell): OfferModalItem {
    const {
      auctionItemUuid,
      localDelivery,
      itemDetail = { modelYear: null, make: null, model: null, trim: null, vin: null },
      inventoryItemUuid
    } = auctionItemSell;
    const { modelYear, make, model, trim, vin } = itemDetail;
    const { buyerOeUuid, leadBuyerOeUuid} = auctionItemSell.offers[0];
    return new OfferModalItem({
      auctionItemUuid,
      buyerOeUuid,
      leadBuyerOeUuid,
      localDelivery,
      modelYear,
      make,
      model,
      trim,
      vin,
      inventoryItemUuid
    });
  }

  static fromInventoryManagementSearchResult(inventoryManagementSearchResult: InventoryManagementSearchResult, auctionItemLatestOffer: InventoryManagementSellerOffer): OfferModalItem {
    const {
      localDelivery,
      itemDetail = { modelYear: null, make: null, model: null, trim: null, vin: null },
      inventoryItemUuid
    } = inventoryManagementSearchResult;
    const auctionItemUuid = inventoryManagementSearchResult.vehicleUuid;
    const { modelYear, make, model, trim, vin } = itemDetail;
    const { buyerOeUuid, leadBuyerOeUuid } = auctionItemLatestOffer;
    return new OfferModalItem({
      auctionItemUuid,
      buyerOeUuid,
      leadBuyerOeUuid,
      localDelivery,
      modelYear,
      make,
      model,
      trim,
      vin,
      inventoryItemUuid
    });
  }
}
